/**
 * Open/closed state for the user-initiated report modal.
 *
 * This is deliberately a tiny publish/subscribe store with no global side effects: it does not
 * patch `console`, `fetch`, `XMLHttpRequest`, `history` or the window error handlers, and it does
 * not persist anything to `localStorage`. The Sentry SDK already records that context
 * automatically as breadcrumbs on whatever event we capture, so the app only needs to know
 * whether the modal is open and which message opened it.
 */

const subscribers = new Set()

let state = { isOpen: false, triggerMessage: null }

function notify() {
  subscribers.forEach((callback) => callback())
}

/**
 * @param {Function} callback - Invoked whenever the modal state changes.
 * @returns {Function} Unsubscribe function.
 */
export function subscribeToReportModal(callback) {
  subscribers.add(callback)
  return () => {
    subscribers.delete(callback)
  }
}

/**
 * @returns {{isOpen: boolean, triggerMessage: string|null}} Current modal state.
 */
export function getReportModalState() {
  return state
}

/**
 * @param {string} [triggerMessage] - Message describing what prompted the report (e.g. the text of
 *   the error notification the user clicked "Report" on).
 */
export function openReportModal(triggerMessage = null) {
  state = { isOpen: true, triggerMessage }
  notify()
}

export function closeReportModal() {
  state = { isOpen: false, triggerMessage: null }
  notify()
}

/** Test helper: drop all subscribers and reset to the closed state. */
export function resetReportModalStore() {
  subscribers.clear()
  state = { isOpen: false, triggerMessage: null }
}
